import { keys } from '@gozoki/api'
import { OPENING_SESSION_DISPLAY_STATUSES } from '@gozoki/api-types'
import { useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'
import z from 'zod'
import { AppContainer } from '~/components/new/app-ui/app-container'
import { AppHeader } from '~/components/new/app-ui/app-header'
import { periodDateRanges, periods } from '~/components/new/app-ui/period-select'
import { Title } from '~/components/new/app-ui/title'
import { PreAuthorizationFilters } from '~/components/routes/pre-authorizations/pre-authorization-filters'
import { PreAuthorizationTable } from '~/components/routes/pre-authorizations/pre-authorization-table'
import { withRoutePermissions } from '~/utils/rbac'
import { getSocket } from '~/utils/websockets/socketManager'

export const Route = createFileRoute('/_authenticated/pre-authorizations/')({
  staticData: {
    permission: ['read', 'cameras'],
  },
  component: PreAuthorizationsPage,
  validateSearch: z.object({
    storeId: z.number().optional(),
    status: z.enum(OPENING_SESSION_DISPLAY_STATUSES).optional(),
    period: z.enum(periods).optional(),
    page: z.number().optional(),
    pageSize: z.number().optional(),
  }),
})
withRoutePermissions(Route)

function PreAuthorizationsPage() {
  const navigate = Route.useNavigate()
  const { storeId, status, period, page = 1, pageSize = 25 } = Route.useSearch()
  const queryClient = useQueryClient()

  useEffect(() => {
    const socket = getSocket()
    const invalidate = () => {
      queryClient.invalidateQueries({ queryKey: keys.openingSessions.all })
    }
    socket.on('pre-authorization-method-selected', invalidate)
    socket.on('pre-authorization-method-verified', invalidate)
    socket.on('opening-event-updated', invalidate)
    return () => {
      socket.off('pre-authorization-method-selected', invalidate)
      socket.off('pre-authorization-method-verified', invalidate)
      socket.off('opening-event-updated', invalidate)
    }
  }, [queryClient])

  const range = period ? periodDateRanges[period]() : undefined

  return (
    <>
      <AppHeader>
        <Title>Pré-autorisations</Title>
      </AppHeader>

      <AppContainer>
        <PreAuthorizationFilters
          storeId={storeId}
          status={status}
          period={period}
          onStoreChange={(v) =>
            navigate({
              search: (prev) => ({
                ...prev,
                storeId: v && v !== -1 ? v : undefined,
                page: undefined,
              }),
            })
          }
          onStatusChange={(v) =>
            navigate({ search: (prev) => ({ ...prev, status: v ?? undefined, page: undefined }) })
          }
          onPeriodChange={(v) =>
            navigate({ search: (prev) => ({ ...prev, period: v ?? undefined, page: undefined }) })
          }
        />

        <PreAuthorizationTable
          storeId={storeId}
          status={status}
          from={range?.start}
          to={range?.end}
          page={page}
          pageSize={pageSize}
          onNavigate={(params) => navigate({ search: (prev) => ({ ...prev, ...params }) })}
        />
      </AppContainer>
    </>
  )
}
